import { Button, Card, CircularProgress, Select, MenuItem, Dialog, DialogTitle, DialogContent, DialogActions } from '@mui/material';
import ArgonBox from 'components/ArgonBox';
import ArgonTypography from 'components/ArgonTypography';
import DashboardLayout from 'examples/LayoutContainers/DashboardLayout';
import PageBlueHeader from 'components/PageBlueHeader';
import Cookies from "js-cookie";
import React, { useEffect, useState } from 'react';
import { useNavigate } from "react-router-dom";
import { useAuth } from "context/AuthContext";
import { useUserData } from "context/UserDataContext";
import { useLanguage } from "context/LanguageContext";
import { jsPDF } from 'jspdf';
import ExcelJS from "exceljs";
import { apiRequest } from "utils/api";
import { formatImporteRetiro, monedaRetiro, formatTotalesPorMoneda, sumarPorMoneda, mergeTotalesPorMoneda } from "utils/formatImporteRetiro";


const Comprobantes = () => {
  const navigate = useNavigate();
  const [mesSeleccionado, setMesSeleccionado] = useState('');
  const [anioSeleccionado, setAnioSeleccionado] = useState('');
  const [loading, setLoading] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [dots, setDots] = useState(0);
  const [nombreEmpresa, setNombreEmpresa] = useState('');
  const [openNoRetirosModal, setOpenNoRetirosModal] = useState(false);

  const { user, loading: authLoading } = useAuth();
  const { userData, empresaData } = useUserData();
  const { t, lang } = useLanguage();
  const dateLocale = lang === 'en' ? 'en-US' : 'es-UY';

    const registrarAccion = async (accion, detalles = '') => {
      try {
        const userId = Cookies.get('userId');
        if (!userId) {
          console.error('No se pudo obtener el userId de las cookies');
          return;
        }
  
        const bodyData = {
          userId,
          accion,
          detalles,
          origen: 'empresas'
        };
        
        // Validar que origen no sea null o undefined
        if (!bodyData.origen || bodyData.origen === null) {
          console.error('ERROR: origen es null o undefined, forzando a "empresas"');
          bodyData.origen = 'empresas';
        }
        
        
        const jsonBody = JSON.stringify(bodyData);
        
        const response = await apiRequest('/registrar-accion', {
          method: 'POST',
          body: jsonBody
        });
        
  
        if (!response.ok) throw new Error('Error al registrar la acción');
      } catch (error) {
        console.error('Error al registrar la acción:', error);
      }
    };

  useEffect(() => {
    // Si Firebase está cargando, esperar
    if (authLoading) {
      return;
    }

    // Si no hay usuario autenticado, redirigir al login
    if (!user) {
      navigate("/authentication/sign-in");
      return;
    }

    // Si hay usuario, cargar datos
    fetchNombreEmpresa();
  }, [navigate, user, authLoading]);

  useEffect(() => {
    if (!downloading) {
      setDots(0);
      return;
    }

    let currentDots = 0;
    let direction = 1; // 1 para incrementar, -1 para decrementar
    const interval = setInterval(() => {
      if (currentDots >= 3) {
        direction = -1;
      } else if (currentDots <= 0) {
        direction = 1;
      }
      currentDots += direction;
      setDots(currentDots);
    }, 200); // Cambia cada 200ms para una animación suave

    return () => clearInterval(interval);
  }, [downloading]);

  const fetchNombreEmpresa = async () => {
    // Usar empresaData del contexto si está disponible
    if (empresaData?.nombre) {
      setNombreEmpresa(empresaData.nombre);
      return;
    }

    // Si no está disponible, intentar obtenerlo usando empresaId
    const empresaId = empresaData?.id || userData?.empresaId;
    if (!empresaId) {
      console.error("No se encontró el ID de la empresa en el contexto.");
      return;
    }

    try {
      setLoading(true);
      const response = await apiRequest(`/datos-empresa/${empresaId}`);

      if (!response.ok) {
        console.error("Error al obtener los datos de la empresa:", response.statusText);
        return;
      }

      const empresa = await response.json();
      setNombreEmpresa(empresa.nombre); // Asignar el nombre de la empresa al estado
    } catch (error) {
      console.error("Error al obtener el nombre de la empresa:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleMesChange = (event) => {
    setMesSeleccionado(event.target.value);
  };

  const handleAnioChange = (event) => {
    const nuevoAnio = event.target.value;
    setAnioSeleccionado(nuevoAnio);
    const anioActual = new Date().getFullYear();
    const mesActual = new Date().getMonth() + 1;
    if (Number(nuevoAnio) === anioActual && mesSeleccionado && Number(mesSeleccionado) > mesActual) {
      setMesSeleccionado('');
    }
  };

  const validarSeleccion = () => {
    if (!mesSeleccionado || !anioSeleccionado) {
      alert("Por favor selecciona un mes y año.");
      return false;
    }
    return true;
  };

  const verificarRetiros = async () => {
    const empresaId = empresaData?.id || userData?.empresaId;
    if (!empresaId) {
      console.error("No se encontró el ID de la empresa en el contexto.");
      return false;
    }
    try {
      const response = await apiRequest(
        `/retiros-empresa?empresaId=${empresaId}&mes=${mesSeleccionado}&anio=${anioSeleccionado}`
      );
      const retiros = await response.json();
      if (retiros.length === 0) {
        setOpenNoRetirosModal(true);
        return false;
      }
      return retiros;
    } catch (error) {
      console.error("Error al verificar retiros:", error);
      return false;
    }
  };

  const verificarMultas = async () => {
    const empresaId = empresaData?.id || userData?.empresaId;
    if (!empresaId) {
      console.error("No se encontró el ID de la empresa en el contexto.");
      return null;
    }
    try {
      const response = await apiRequest(
        `/multas/empresa/${empresaId}?mes=${mesSeleccionado}&anio=${anioSeleccionado}`
      );
      if (!response.ok) {
        return null;
      }
      const multas = await response.json();
      return multas.length > 0 ? multas[0] : null;
    } catch (error) {
      console.error("Error al verificar multas:", error);
      return null;
    }
  };

  const generarHistorialExcel = async (retiros) => {
    const empresaId = empresaData?.id || userData?.empresaId;
    if (!empresaId) {
      console.error("No se encontró el ID de la empresa en el contexto.");
      return;
    }

    try {
      const multa = await verificarMultas();
      
      // Obtener usuarios si no están incluidos en los retiros
      let usuariosMap = {};
      if (retiros.length > 0 && !retiros[0].User) {
        try {
          const usuariosResponse = await apiRequest(`/usuarios-empresa/${empresaId}`);
          if (usuariosResponse.ok) {
            const usuarios = await usuariosResponse.json();
            usuariosMap = usuarios.reduce((acc, usuario) => {
              if (usuario.id) {
                acc[usuario.id] = usuario;
              }
              return acc;
            }, {});
            
            // Obtener usuarios faltantes
            const userIdsRetiros = [...new Set(retiros.map(r => r.userId))];
            const userIdsFaltantes = userIdsRetiros.filter(userId => !usuariosMap[userId]);
            
            if (userIdsFaltantes.length > 0) {
              const usuariosFaltantesPromises = userIdsFaltantes.map(async (userId) => {
                try {
                  const response = await apiRequest(`/datos-usuario-id/${userId}`);
                  if (response.ok) {
                    const usuario = await response.json();
                    return usuario;
                  }
                  return null;
                } catch (error) {
                  console.error(`Error obteniendo usuario ${userId}:`, error);
                  return null;
                }
              });
              
              const usuariosFaltantes = (await Promise.all(usuariosFaltantesPromises)).filter(u => u !== null);
              usuariosFaltantes.forEach(usuario => {
                if (usuario.id) {
                  usuariosMap[usuario.id] = usuario;
                }
              });
            }
          }
        } catch (error) {
          console.error('Error al obtener usuarios:', error);
        }
      }
      
      const workbook = new ExcelJS.Workbook();
      const sheet = workbook.addWorksheet(t('comprobantes.excel.sheetName'));
  
      // Título
      sheet.mergeCells("A1:D1");
      const titleCell = sheet.getCell("A1");
      const mesNombre = (t(`month.${mesSeleccionado}`) || "").toUpperCase();
      const mesSiguiente = mesSeleccionado === 12 ? 1 : mesSeleccionado + 1;
      const mesSiguienteNombre = (t(`month.${mesSiguiente}`) || "").toUpperCase();

      titleCell.value = t('comprobantes.excel.title', { month: mesNombre, company: nombreEmpresa });
      titleCell.font = { bold: true, size: 14, color: { argb: "FFFFFF" } };
      titleCell.alignment = { horizontal: "center", vertical: "middle" };
      titleCell.fill = {
        type: "pattern",
        pattern: "solid",
        fgColor: { argb: "0000FF" },
      };
  
      // Encabezados
      const headerRow = sheet.addRow([
        t('comprobantes.excel.fullName'),
        t('comprobantes.excel.amountWithdrawn'),
        t('comprobantes.excel.date'),
        t('comprobantes.excel.total'),
      ]);
      headerRow.eachCell((cell) => {
        cell.font = { bold: true, color: { argb: "FFFFFF" } };
        cell.alignment = { horizontal: "center", vertical: "middle" };
        cell.fill = {
          type: "pattern",
          pattern: "solid",
          fgColor: { argb: "808080" },
        };
        cell.border = {
          top: { style: "thin" },
          left: { style: "thin" },
          bottom: { style: "thin" },
          right: { style: "thin" },
        };
      });
  
      // Agrupar retiros por usuario
      const agrupados = {};
      retiros.forEach((retiro) => {
        let nombreCompleto = '';
        if (retiro.User && retiro.User.nombre && retiro.User.apellido) {
          nombreCompleto = `${retiro.User.nombre} ${retiro.User.apellido}`;
        } else if (usuariosMap[retiro.userId]) {
          const usuario = usuariosMap[retiro.userId];
          nombreCompleto = `${usuario.nombre || ''} ${usuario.apellido || ''}`.trim();
        } else {
          nombreCompleto = t('comprobantes.excel.unknownUser', { id: retiro.userId });
        }
        
        if (!agrupados[nombreCompleto]) {
          agrupados[nombreCompleto] = { retiros: [] };
        }
        agrupados[nombreCompleto].retiros.push({
          fecha: new Date(retiro.fecha).toLocaleDateString(dateLocale),
          cantidad: retiro.cantidad,
          moneda: monedaRetiro(retiro),
        });
      });

      Object.values(agrupados).forEach((datos) => {
        datos.totalesPorMoneda = sumarPorMoneda(datos.retiros, {
          getMoneda: (item) => item.moneda,
        });
      });
  
      // Generar filas agrupadas con formato
      let totalGeneralPorMoneda = {};
      let colorAlternado = false;
      let startRow = 3; // Fila inicial de datos (después del título y encabezados)
  
      for (const [nombre, datos] of Object.entries(agrupados)) {
        let esPrimeraFila = true;
        const colorFondo = colorAlternado ? "D9E1F2" : "FFFFFF";
        datos.retiros.forEach((retiro, index) => {
          const totalUsuario = formatTotalesPorMoneda(datos.totalesPorMoneda, { separator: "\n" });
          const row = sheet.addRow([
            esPrimeraFila ? nombre : "",
            formatImporteRetiro(retiro.cantidad, retiro.moneda),
            retiro.fecha,
            esPrimeraFila ? totalUsuario : "",
          ]);
          row.eachCell((cell, colNumber) => {
            cell.alignment = {
              horizontal: "left",
              vertical: "middle",
              wrapText: colNumber === 4,
            };
            cell.fill = {
              type: "pattern",
              pattern: "solid",
              fgColor: { argb: colorFondo },
            };
            if (colNumber === 4 && !esPrimeraFila) {
              cell.value = null;
            }
          });
          esPrimeraFila = false;
        });
        
  
        // Hacer merge de la celda "Total" para el usuario
        if (datos.retiros.length > 1) {
          sheet.mergeCells(`D${startRow}:D${startRow + datos.retiros.length - 1}`);
        }
  
        startRow += datos.retiros.length;
        totalGeneralPorMoneda = mergeTotalesPorMoneda(totalGeneralPorMoneda, datos.totalesPorMoneda);
        colorAlternado = !colorAlternado; // Alternar color
      }
  
      // Agregar fila de multa si existe
      if (multa && multa.importe) {
        const multaRow = sheet.addRow([
          t('comprobantes.excel.lateFee'),
          `$ ${multa.importe.toLocaleString(dateLocale)}`,
          new Date(multa.createdAt).toLocaleDateString(dateLocale),
          `$ ${multa.importe.toLocaleString(dateLocale)}`
        ]);
        multaRow.eachCell((cell) => {
          cell.font = { bold: false, color: { argb: "ad3232" } };
          cell.fill = {
            type: "pattern",
            pattern: "solid",
            fgColor: { argb: "ffffff" },
          };
        });
        totalGeneralPorMoneda = mergeTotalesPorMoneda(totalGeneralPorMoneda, { UYU: multa.importe });
      }
  
      // Fila de total general
      const totalGeneralTexto = formatTotalesPorMoneda(totalGeneralPorMoneda, { separator: "\n" });
      const totalRow = sheet.addRow(["", "", t('comprobantes.excel.grandTotal'), totalGeneralTexto]);
      totalRow.eachCell((cell, colNumber) => {
        if (colNumber === 4) {
          cell.font = { bold: true };
          cell.alignment = { horizontal: "left", vertical: "middle", wrapText: true };
          cell.fill = {
            type: "pattern",
            pattern: "solid",
            fgColor: { argb: "D9E1F2" },
          };
        }
      });
  
      // Ajustar columnas
      sheet.columns.forEach((column) => {
        column.width = 20;
      });
  
      // Nota al final
      const noteRow = sheet.addRow([
        t('comprobantes.excel.paymentNote', { month: mesSiguienteNombre, year: anioSeleccionado }),
      ]);
      noteRow.getCell(1).fill = {
        type: "pattern",
        pattern: "solid",
        fgColor: { argb: "FFD966" },
      };
      noteRow.getCell(1).font = { italic: true, color: { argb: "000000" } };
      sheet.mergeCells(`A${noteRow.number}:D${noteRow.number}`);
  
      const additionalNoteRow = sheet.addRow([
        t('comprobantes.excel.legalNote'),
      ]);
      additionalNoteRow.getCell(1).fill = {
        type: "pattern",
        pattern: "solid",
        fgColor: { argb: "FFFFFF" },
      };
      additionalNoteRow.getCell(1).font = { italic: true, color: { argb: "000000" } };
      sheet.mergeCells(`A${additionalNoteRow.number}:D${additionalNoteRow.number}`);
  
      // Guardar archivo
      const buffer = await workbook.xlsx.writeBuffer();
      const blob = new Blob([buffer], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" });
      const url = URL.createObjectURL(blob);
  
      // Descargar archivo
      const a = document.createElement("a");
      a.href = url;
      a.download = `${nombreEmpresa}-${t('comprobantes.fileSuffix')}-${mesNombre}-${anioSeleccionado}.xlsx`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Error al generar el Excel estilizado:", error);
    }
   
  };

  const generarHistorialPDF = async (retiros) => {
    const empresaId = empresaData?.id || userData?.empresaId;
    if (!empresaId) {
      console.error("No se encontró el ID de la empresa en el contexto.");
      return;
    }

    const doc = new jsPDF();
    const imgData = "/favicon.png";
    const mesNombre = t(`month.${mesSeleccionado}`);

    try {
      if (retiros.length === 0) {
        return;
      }

      // Obtener usuarios si no están incluidos en los retiros
      let usuariosMap = {};
      if (retiros.length > 0 && !retiros[0].User) {
        try {
          const usuariosResponse = await apiRequest(`/usuarios-empresa/${empresaId}`);
          if (usuariosResponse.ok) {
            const usuarios = await usuariosResponse.json();
            usuariosMap = usuarios.reduce((acc, usuario) => {
              if (usuario.id) {
                acc[usuario.id] = usuario;
              }
              return acc;
            }, {});
            
            // Obtener usuarios faltantes
            const userIdsRetiros = [...new Set(retiros.map(r => r.userId))];
            const userIdsFaltantes = userIdsRetiros.filter(userId => !usuariosMap[userId]);
            
            if (userIdsFaltantes.length > 0) {
              const usuariosFaltantesPromises = userIdsFaltantes.map(async (userId) => {
                try {
                  const response = await apiRequest(`/datos-usuario-id/${userId}`);
                  if (response.ok) {
                    const usuario = await response.json();
                    return usuario;
                  }
                  return null;
                } catch (error) {
                  console.error(`Error obteniendo usuario ${userId}:`, error);
                  return null;
                }
              });
              
              const usuariosFaltantes = (await Promise.all(usuariosFaltantesPromises)).filter(u => u !== null);
              usuariosFaltantes.forEach(usuario => {
                if (usuario.id) {
                  usuariosMap[usuario.id] = usuario;
                }
              });
            }
          }
        } catch (error) {
          console.error('Error al obtener usuarios:', error);
        }
      }

      const retirosAgrupados = retiros.reduce((acc, retiro) => {
        const { userId, User } = retiro;
        let usuarioData = User;
        
        // Si no hay User en el retiro, usar el mapa de usuarios
        if (!usuarioData && usuariosMap[userId]) {
          usuarioData = usuariosMap[userId];
        }
        
        if (!acc[userId] && usuarioData) {
          acc[userId] = { ...usuarioData, retiros: [] };
        }
        if (usuarioData) {
          acc[userId].retiros.push(retiro);
        }
        return acc;
      }, {});

      let isFirstPage = true;

      for (const usuarioId in retirosAgrupados) {
        const usuario = retirosAgrupados[usuarioId];

        // Si falta el documento, intentar obtenerlo desde la API
        if (!usuario.documento) {
          try {
            const response = await apiRequest(`/datos-usuario-id/${usuarioId}`);
            if (response.ok) {
              const datosUsuario = await response.json();
              if (datosUsuario?.documento) {
                usuario.documento = datosUsuario.documento;
              }
            }
          } catch (e) {
            console.error(`No se pudo obtener el documento para el usuario ${usuarioId}`, e);
          }
        }

        const totalRetirado = sumarPorMoneda(usuario.retiros, {
          cantidadKey: "cantidad",
          getMoneda: monedaRetiro,
        });

        if (!isFirstPage) {
          doc.addPage();
        }
        isFirstPage = false;

        // Header en cada página
        doc.addImage(imgData, 'PNG', 10, 10, 30, 30);
        doc.setTextColor(40, 40, 40);
        doc.setFont('helvetica', 'bold');
        doc.setFontSize(24);
        doc.text(t('pdf.retiros.title', { month: mesNombre }), 50, 20);
        doc.setFontSize(12);
        doc.text(t('pdf.retiros.support'), 50, 30);

        let y = 45; 
        doc.setFontSize(16);
        doc.text(`${usuario.nombre} ${usuario.apellido}`, 10, y);
        doc.setFontSize(12);
        doc.text(`${t('pdf.retiros.document')}: ${usuario.documento || ''}`, 10, y + 10);
        doc.text(
          `${t('pdf.retiros.totalWithdrawn')}: ${formatTotalesPorMoneda(totalRetirado)}`,
          10,
          y + 20
        );
        y += 35;

        // Tabla de detalles de retiros
        doc.setFont('helvetica', 'normal');
        doc.setFontSize(10);
        doc.setFillColor(230, 230, 230);
        doc.rect(10, y, 190, 8, 'F');
        doc.text(t('pdf.retiros.date'), 15, y + 5);
        doc.text(t('pdf.retiros.hour'), 65, y + 5);
        doc.text(t('pdf.retiros.amount'), 150, y + 5);
        y += 13;

        for (const retiro of usuario.retiros) {
          const fechaHora = new Date(retiro.fecha);
          const fecha = fechaHora.toLocaleDateString(dateLocale);
          const hora = fechaHora.toLocaleTimeString(dateLocale, { hour: '2-digit', minute: '2-digit' }); 
          const importe = Math.round(retiro.cantidad); 

          doc.text(fecha, 15, y);
          doc.text(hora, 65, y);
          doc.text(formatImporteRetiro(importe, monedaRetiro(retiro)), 150, y);
          y += 10;

          if (y > 270) {
            doc.addPage();
            doc.addImage(imgData, 'PNG', 10, 10, 30, 30);
            doc.setFontSize(24);
            doc.text(t('pdf.retiros.title', { month: mesNombre }), 50, 20);
            doc.setFontSize(12);
            doc.text(t('pdf.retiros.support'), 50, 30);
            y = 50;

            doc.setFillColor(230, 230, 230);
            doc.rect(10, y, 190, 8, 'F');
            doc.text(t('pdf.retiros.date'), 15, y + 5);
            doc.text(t('pdf.retiros.hour'), 65, y + 5);
            doc.text(t('pdf.retiros.amount'), 150, y + 5);
            y += 13;
          }
        }
      }

      doc.save(`${nombreEmpresa}-${t('comprobantes.fileSuffix')}-${mesNombre}-${anioSeleccionado}.pdf`);
    } catch (error) {
      console.error("Error al generar el PDF:", error);
    }
  };

  return (
    <div style={{ position: 'relative' }}>
      {loading && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            zIndex: 9999,
          }}
        >
          <CircularProgress color="primary" />
        </div>
      )}

      <DashboardLayout>
        <PageBlueHeader
          title={t('comprobantes.title')}
          subtitle={t('comprobantes.pageSubtitle')}
        />
        <ArgonBox pt={2} pb={6}>
          <Card>
            <ArgonBox p={3}>
              <ArgonTypography variant="body2" sx={{ fontWeight: 'bold', mb: 1, fontSize: '0.8rem' }}>
                {t('comprobantes.month')}
              </ArgonTypography>
              <Select
                value={mesSeleccionado}
                onChange={handleMesChange}
                displayEmpty
                fullWidth
                variant="outlined"
                sx={{
                  '& .MuiSelect-select': {
                    fontSize: '0.9rem',
                    py: 1.2,
                    width: '100% !important',
                    paddingLeft: '2px !important',
                  }
                }}
              >
                <MenuItem value="" disabled>
                  {t('comprobantes.selectMonth')}
                </MenuItem>
                {[1,2,3,4,5,6,7,8,9,10,11,12].map((mesNum) => {
                  const anioActual = new Date().getFullYear();
                  const mesActual = new Date().getMonth() + 1;
                  if (anioSeleccionado && Number(anioSeleccionado) === anioActual && mesNum > mesActual) {
                    return null;
                  }
                  return (
                    <MenuItem key={mesNum} value={mesNum}>
                      {t(`month.${mesNum}`)}
                    </MenuItem>
                  );
                })}
              </Select>

              <ArgonTypography variant="body2" sx={{ fontWeight: 'bold', mb: 1, fontSize: '0.8rem', mt: 2 }}>
                {t('comprobantes.year')}
              </ArgonTypography>
              <Select
                value={anioSeleccionado}
                onChange={handleAnioChange}
                displayEmpty
                fullWidth
                variant="outlined"
                sx={{
                  '& .MuiSelect-select': {
                    fontSize: '0.9rem',
                    py: 1.2,
                    width: '100% !important',
                    paddingLeft: '2px !important',
                  }
                }}
              >
                <MenuItem value="" disabled>
                  {t('comprobantes.selectYear')}
                </MenuItem>
                {Array.from(
                  { length: new Date().getFullYear() - 2024 + 1 },
                  (_, i) => 2024 + i
                ).map((anio) => (
                  <MenuItem key={anio} value={anio}>
                    {anio}
                  </MenuItem>
                ))}
              </Select>

              <ArgonBox display="flex" justifyContent="flex-end">
                <Button
                  variant="contained"
                  disabled={!mesSeleccionado || !anioSeleccionado || downloading}
                  onClick={async () => {
                    if (!validarSeleccion()) {
                      return;
                    }
                    
                    setDownloading(true);
                    try {
                      const retiros = await verificarRetiros();
                      if (!retiros) {
                        setDownloading(false);
                        return;
                      }

                      await generarHistorialExcel(retiros);
                      await generarHistorialPDF(retiros);
                      registrarAccion('Descarga de comprobantes', 'Se descargaron los comprobantes y el Excel');
                    } catch (error) {
                      console.error('Error al descargar archivos:', error);
                    } finally {
                      setDownloading(false);
                    }
                  }}
                  sx={{
                    mt: 3,
                    px: 4,
                    py: 1.5,
                    fontSize: '1rem',
                    fontWeight: 'normal',
                    backgroundColor: '#f8f8f8',
                    color: '#333333',
                    border: '1px solid #d1d3d4',
                    borderRadius: 2,
                    textTransform: 'none',
                    boxShadow: 'none',
                    '&:hover': {
                      backgroundColor: '#f0f0f0',
                      boxShadow: 'none',
                    },
                    '&:disabled': {
                      backgroundColor: 'rgba(80, 80, 80, 0.04)',
                      color: '#3B3B3B',
                      border: 'none',
                      boxShadow: 'none',
                    },
                    ...((mesSeleccionado && anioSeleccionado && !downloading) && {
                      backgroundColor: '#1E86F6',
                      color: 'white',
                      fontWeight: 'bold',
                      border: 'none',
                      '&:hover': {
                        backgroundColor: '#0d6efd',
                        boxShadow: 'none',
                      },
                    }),
                    ...(downloading && {
                      backgroundColor: '#1E86F6',
                      color: 'white',
                      fontWeight: 'bold',
                      border: 'none',
                      opacity: 0.7,
                      cursor: 'not-allowed',
                    })
                  }}
                >
                  {downloading ? (
                    <>
                      {t('comprobantes.downloading')}
                      <span style={{ display: 'inline-block', width: '20px', textAlign: 'left' }}>
                        {'.'.repeat(dots)}
                      </span>
                    </>
                  ) : t('comprobantes.download')}
                </Button>
              </ArgonBox>
            </ArgonBox>
          </Card>
        </ArgonBox>
      </DashboardLayout>

      <Dialog
        open={openNoRetirosModal}
        onClose={() => setOpenNoRetirosModal(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle sx={{ fontWeight: 700 }}>
          {t('comprobantes.noWithdrawals.title')}
        </DialogTitle>
        <DialogContent>
          <ArgonTypography variant="body2">
            {t('comprobantes.noWithdrawals.desc')}
          </ArgonTypography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            variant="contained"
            onClick={() => setOpenNoRetirosModal(false)}
            sx={{
              backgroundColor: '#1E86F6',
              textTransform: 'none',
              '&:hover': {
                backgroundColor: '#0d6efd',
              },
            }}
          >
            {t('comprobantes.noWithdrawals.ok')}
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default Comprobantes;
